import type { ElementType, ReactNode } from 'react';

import { classFilter } from '@shieldpay/theme-provider-ui/utilities';

import type { BoxProps } from '../box/box';
import { Box } from '../box/box';
import { getBoxStyles } from '../box/get-box-styles';
import { Text } from '../text/text';
import { getTextStyles } from '../text/text-styles';

import * as styles from './primary-nav.css';

export type PrimaryNavProps = Pick<
  BoxProps,
  'background' | 'gridItem' | 'children'
>;

export interface NavItem {
  children: ReactNode;
  disabled?: boolean;
  to: string;
  NavLink: ElementType;
}

export interface NavHeadingProps {
  children: ReactNode;
}

const PrimaryNavHeading = ({ children }: NavHeadingProps) => (
  <Text
    Component="li"
    variant="section"
    color="neutral250"
    padding={{ left: 'basePos3', y: 'base' }}
  >
    {children}
  </Text>
);

const linkPadding = { left: 'basePos3', y: 'base' } as const;

const PrimaryNavItem = ({
  disabled = false,
  children,
  to,
  NavLink,
}: NavItem) => {
  const className = classFilter([styles.link, !disabled && styles.active]);

  return (
    <Box Component="li">
      {disabled ? (
        <Text color="neutral250" padding={linkPadding} className={className}>
          {children}
        </Text>
      ) : (
        <NavLink
          className={classFilter([
            className,
            getTextStyles({
              variant: 'bodyMedium',
            }),
            getBoxStyles({ color: 'neutral000', padding: linkPadding }),
          ])}
          to={to}
          // Disabling prefetch for now. I thought it would prefetch the data for immediately switching
          // but all that seems to happen is that it calls the loader on hover, then calls it again on click
          // this might be a subtle react router bug so we should revisit this later
          // prefetch="intent"
        >
          {children}
        </NavLink>
      )}
    </Box>
  );
};

export const PrimaryNav = ({
  children,
  background,
  gridItem,
}: PrimaryNavProps) => {
  return (
    <Box
      background={background}
      Component="nav"
      gridItem={gridItem}
      className={styles.container}
      // TODO i18n
      aria-label="Primary navigation"
    >
      <Box Component="ul">{children}</Box>
    </Box>
  );
};

PrimaryNav.Item = PrimaryNavItem;
PrimaryNav.Heading = PrimaryNavHeading;
